/**
 * A session's mark: a flat shape peeking up from a tile, drawn from its id
 * (RFC 0311 3a).
 *
 * PURE ON PURPOSE. The mark is arithmetic on the session id, so it needs no
 * storage, no fetch and no sync, and the same session draws the same mark in
 * every surface and on every device. Nothing may store, override or reroll it:
 * a reroll means a seed, and a seed is state.
 *
 * The design is option E of the prototype page (`session-avatars.html`), after
 * the #Kids poster: a big flat shape rising from the bottom of a soft tile and
 * cropped by it, with tiny eyes near the top. 8 colors, 6 shapes, 3 eye styles.
 * The picks are independent digits of one hash, so each spreads evenly alone.
 */

/** What the hash picks. Exported for tests, not for callers. */
export interface Mark {
  /** Index into MARK_COLORS. */
  color: number
  /** Index into MARK_SHAPES. */
  shape: number
  /** Index into MARK_FACES. */
  face: number
  /** Where the dot eyes look, for the one face that looks anywhere. */
  look: -1 | 0 | 1
}

/** The poster's flat primaries, and five more at the same weight. */
export const MARK_COLORS = [
  '#FF5A00',
  '#0019FF',
  '#FF0A8C',
  '#00A651',
  '#FFB400',
  '#7B2CFF',
  '#E8231A',
  '#00A3E0',
] as const

export const MARK_SHAPES = [
  'arch',
  'boot',
  'peak',
  'dome',
  'pill',
  'hill',
] as const

export const MARK_FACES = ['dots', 'slant', 'sleepy'] as const

const INK = '#1d1b22'

/**
 * FNV-1a with a murmur finalizer. FNV alone clusters on ids that differ in one
 * character (sess-1, sess-2), which bunched the colors in the prototype.
 */
function hash(str: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return h >>> 0
}

export function markFor(id: string): Mark {
  const h = hash(id)
  const colors = MARK_COLORS.length
  const shapes = MARK_SHAPES.length
  const faces = MARK_FACES.length
  const look = Math.floor(h / (colors * shapes * faces)) % 3
  return {
    color: h % colors,
    shape: Math.floor(h / colors) % shapes,
    face: Math.floor(h / (colors * shapes)) % faces,
    look: look === 0 ? -1 : look === 1 ? 0 : 1,
  }
}

/** The tile: the shape's own color, mostly white, so a dark sidebar gets no bright square. */
function tint(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16)
  return `#${[16, 8, 0]
    .map((s) => Math.round(((n >> s) & 255) * 0.14 + 255 * 0.86))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`
}

/**
 * Each shape on a 32 box, rising past the bottom edge so the tile crops it,
 * and where its eyes sit: high and a little off-centre, as on the poster.
 */
function shapeArt(
  shape: (typeof MARK_SHAPES)[number],
  fill: string,
): [string, number, number] {
  switch (shape) {
    case 'arch':
      return ['<path d="M5,36 V17.5 A11,11 0 0 1 27,17.5 V36 Z"/>', 15, 15]
    case 'boot':
      return [
        '<path d="M3,36 V15.5 A9.5,9.5 0 0 1 22,15.5 V36 Z"/><rect x="14" y="23.5" width="17" height="14" rx="4.5"/>',
        11.5,
        14,
      ]
    case 'peak':
      // A stroke in the fill color rounds the corners without a longer path.
      return [
        `<path d="M16,6.5 L31.5,33 H0.5 Z" stroke="${fill}" stroke-width="5" stroke-linejoin="round"/>`,
        17,
        17.5,
      ]
    case 'dome':
      return ['<circle cx="16" cy="33" r="14.5"/>', 16, 25]
    case 'pill':
      return [
        '<rect x="8.5" y="5.5" width="15" height="32" rx="7.5"/>',
        16,
        12.5,
      ]
    case 'hill':
      return ['<circle cx="23" cy="37" r="22"/>', 15.5, 22]
  }
}

function faceArt(
  face: (typeof MARK_FACES)[number],
  ex: number,
  ey: number,
  look: number,
): string {
  const dot = (x: number, y: number, r = 0) =>
    `<ellipse cx="${x}" cy="${y}" rx="1.6" ry="2.1" transform="rotate(${r} ${x} ${y})" fill="${INK}"/>`
  const lid = (x: number, y: number) =>
    `<path d="M${x - 1.9},${y} Q${x},${y + 2.2} ${x + 1.9},${y}" stroke="${INK}" stroke-width="1.7" stroke-linecap="round" fill="none"/>`
  switch (face) {
    case 'dots':
      return dot(ex - 2.9 + look, ey) + dot(ex + 2.9 + look, ey)
    case 'slant':
      return dot(ex - 2.6, ey - 0.9, -18) + dot(ex + 2.6, ey + 0.9, -18)
    case 'sleepy':
      return lid(ex - 2.9, ey) + lid(ex + 2.9, ey)
  }
}

/** Pure. No state, no IO. The same id always returns the same SVG. */
export function sessionAvatar(id: string, size: number): string {
  const mark = markFor(id)
  const fill = MARK_COLORS[mark.color]
  const [art, ex, ey] = shapeArt(MARK_SHAPES[mark.shape], fill)
  const face = faceArt(MARK_FACES[mark.face], ex, ey, mark.look)
  // The clip id is local to this SVG: SessionMark draws it as its own image.
  return `<svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><clipPath id="t"><rect width="32" height="32" rx="7"/></clipPath><g clip-path="url(#t)"><rect width="32" height="32" fill="${tint(fill)}"/><g fill="${fill}">${art}</g>${face}</g></svg>`
}
